import { useState, useEffect, useCallback } from 'react';

const DB_NAME = 'feconciencia-offline-v2';
const STORE_NAME = 'offline-content';

const openDB = () => new Promise((resolve, reject) => {
  const req = indexedDB.open(DB_NAME, 1);
  req.onerror = () => reject(req.error);
  req.onsuccess = () => resolve(req.result);
  req.onupgradeneeded = (e) => {
    const db = e.target.result;
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      db.createObjectStore(STORE_NAME, { keyPath: 'id' });
    }
  };
});

const useOfflineContent = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [downloads, setDownloads] = useState([]);
  const [downloading, setDownloading] = useState({});

  useEffect(() => {
    const online = () => setIsOnline(true);
    const offline = () => setIsOnline(false);
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    loadDownloads();
    return () => { window.removeEventListener('online', online); window.removeEventListener('offline', offline); };
  }, []);

  const loadDownloads = async () => {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.getAll();
      req.onsuccess = () => setDownloads(req.result || []);
    } catch { /* db not ready */ }
  };

  const downloadContent = useCallback(async (item) => {
    // item: { id, title, url, type: 'pdf'|'audio'|'image', courseId }
    if (!item.url || !item.id) return;
    setDownloading(prev => ({ ...prev, [item.id]: { progress: 0, status: 'downloading' } }));

    try {
      const response = await fetch(item.url);
      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);

      const record = {
        id: item.id,
        title: item.title || item.id,
        type: item.type || 'pdf',
        courseId: item.courseId || '',
        size: blob.size,
        blob: blob,
        downloadedAt: new Date().toISOString(),
      };
      store.put(record);

      await new Promise((resolve, reject) => { tx.oncomplete = resolve; tx.onerror = reject; });

      // Also cache in SW for URL-based access
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'CACHE_CONTENT', urls: [item.url] });
      }

      setDownloading(prev => ({ ...prev, [item.id]: { progress: 100, status: 'done' } }));
      await loadDownloads();
      setTimeout(() => setDownloading(prev => { const n = { ...prev }; delete n[item.id]; return n; }), 1500);
    } catch (err) {
      setDownloading(prev => ({ ...prev, [item.id]: { progress: 0, status: 'error', error: err.message } }));
    }
  }, []);

  const removeContent = useCallback(async (id) => {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(id);
      await new Promise((resolve) => { tx.oncomplete = resolve; });
      await loadDownloads();
    } catch { /* error */ }
  }, []);

  const getOfflineUrl = useCallback(async (id) => {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(id);
      return new Promise((resolve) => {
        req.onsuccess = () => {
          if (req.result?.blob) resolve(URL.createObjectURL(req.result.blob));
          else resolve(null);
        };
        req.onerror = () => resolve(null);
      });
    } catch { return null; }
  }, []);

  const isDownloaded = useCallback((id) => downloads.some(d => d.id === id), [downloads]);

  const getStorageUsed = useCallback(() => {
    return downloads.reduce((sum, d) => sum + (d.size || 0), 0);
  }, [downloads]);

  const clearAll = useCallback(async () => {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).clear();
      await new Promise(r => { tx.oncomplete = r; });
      setDownloads([]);
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CONTENT_CACHE' });
      }
    } catch { /* error */ }
  }, []);

  return { isOnline, downloads, downloading, downloadContent, removeContent, getOfflineUrl, isDownloaded, getStorageUsed, clearAll };
};

export default useOfflineContent;
